import React, { useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';

import { useAgentChatSession } from './AgentChatSessionProvider';
import FlushInput from './FlushInput';

const MAX_AGENT_THREAD_TITLE_LENGTH = 255;

type Props = {
  className?: string;
  testId: string;
  threadId: string;
  title: string;
};

export type AgentThreadTitleDependencies = {
  renameThread: (threadId: string, title: string) => ReturnType<ReturnType<typeof useAgentChatSession>['renameThread']>;
  translate: (key: string) => string;
};

type ViewProps = Props & AgentThreadTitleDependencies;

/** The shared inline title editor for full-page and dock Agent conversations. */
export const EditableAgentThreadTitleView: React.FC<ViewProps> = ({
  className,
  testId,
  threadId,
  title,
  renameThread,
  translate,
}) => {
  const [draft, setDraft] = useState(title);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const cancel = () => {
    setDraft(title);
    setError(null);
    setEditing(false);
  };
  const save = async () => {
    const nextTitle = draft.trim();
    if (!nextTitle || nextTitle === title || saving) {
      if (nextTitle === title) {
        cancel();
      }
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await renameThread(threadId, nextTitle);
      result.match(
        () => setEditing(false),
        (failure) => setError(failure.message)
      );
    } catch {
      setError(translate('hiring-dashboard-ai-assistant:agent.error.generic'));
    } finally {
      setSaving(false);
      inputRef.current?.focus();
    }
  };

  if (!editing) {
    const startEditing = () => {
      setDraft(title);
      setError(null);
      setEditing(true);
    };
    return (
      <button
        aria-label={translate('hiring-dashboard-ai-assistant:agent.threads.rename')}
        className={className}
        data-testid={testId}
        onClick={startEditing}
        type="button"
      >
        <span className="block min-w-0 truncate">{title}</span>
      </button>
    );
  }

  return (
    <div className={className}>
      <FlushInput
        ref={inputRef}
        aria-invalid={Boolean(error)}
        aria-label={translate('hiring-dashboard-ai-assistant:agent.threads.rename')}
        autoFocus
        className="h-8 min-w-0 w-full rounded-none text-sm"
        disabled={saving}
        maxLength={MAX_AGENT_THREAD_TITLE_LENGTH}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void save();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            cancel();
          }
        }}
        value={draft}
      />
      {error && (
        <span className="sr-only" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

const EditableAgentThreadTitle: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { renameThread } = useAgentChatSession();
  return <EditableAgentThreadTitleView {...props} renameThread={renameThread} translate={(key) => t(key)} />;
};

export default EditableAgentThreadTitle;
